import { defineStore } from 'pinia'
import { signInInvestor, getCompanyProfiles } from '@/assets/api/auth'
import type { MeState, InvestorSignInRequest } from '@/types/auth'

const setCookie = (name: string, value: string, hours: number) => {
    const date = new Date()
    date.setTime(date.getTime() + hours * 60 * 60 * 1000)
    const expires = `expires=${date.toUTCString()}`
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict;Secure`
}

const getCookie = (name: string): string | null => {
    const nameEQ = name + '='
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i]
        while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length)
        if (cookie.indexOf(nameEQ) === 0) return cookie.substring(nameEQ.length, cookie.length)
    }
    return null
}


const deleteCookie = (name: string) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;SameSite=Strict;Secure`
}

export const useMeStore = defineStore('me', {
    state: (): MeState => ({
        user: null,
        token: null,
        isNewUser: false,
        isLoading: false,
        error: null,
        isAuthenticated: false,
        companyProfiles: null,
    }),

    getters: {
        fullName: (state) => {
            if (!state.user) return ''
            return `${state.user.firstName} ${state.user.lastName}`
        },
        isApproved: (state) => state.user?.isApproved ?? false,
        isActivated: (state) => state.user?.isActivated ?? false,
    },

    actions: {
        setIsNewUser(value: boolean) {
            this.isNewUser = value
        },

        async login(credentials: InvestorSignInRequest) {
            this.isLoading = true
            this.error = null

            try {
                const response = await signInInvestor(credentials)

                if (response.isSuccess && response.data) {
                    this.user = response.data.investor
                    this.token = response.data.accessToken
                    this.isAuthenticated = true
                    this.error = null

                    setCookie('x-member-authorization', response.data.accessToken, 3)
                    localStorage.setItem('userProfile', JSON.stringify(response.data.investor))

                    return { success: true, user: this.user }
                } else {
                    this.error = response.messages.join(', ') || 'Login failed'
                    this.isAuthenticated = false

                    return {
                        success: false,
                        error: this.error,
                    }
                }
            } catch (error) {
                console.error('Store: Login error:', error)
                this.error = 'An unexpected error occurred during login'
                this.isAuthenticated = false

                return {
                    success: false,
                    error: this.error,
                }
            } finally {
                this.isLoading = false
            }
        },

        logout() {
            this.user = null
            this.token = null
            this.isAuthenticated = false
            this.error = null

            deleteCookie('x-member-authorization')
            localStorage.removeItem('userProfile')
        },

        restoreSession() {
            const token = getCookie('x-member-authorization')
            const userProfile = localStorage.getItem('userProfile')

            if (token && userProfile) {
                try {
                    this.token = token
                    this.user = JSON.parse(userProfile)
                    this.isAuthenticated = true
                } catch (error) {
                    console.error('Store: Failed to restore session:', error)
                    this.logout()
                }
            } else if (!token) {
                this.logout()
            }
        },

        clearError() {
            this.error = null
        },
        async fetchCompanyProfiles() {
            this.isLoading = true
            this.error = null
            try {
                const res = await getCompanyProfiles(getCookie('x-member-authorization'))
                this.isLoading = false
                if (res && res.isSuccess) {
                    this.companyProfiles = res.data
                    return res.data
                }
                this.error = res?.error || 'Failed to fetch company profiles'
                return null
            } catch (error) {
                console.error('fetchCompanyProfiles error:', error)
                this.error = 'Failed to fetch company profiles'
                this.isLoading = false
                return null
            }
        },
    },
})
